import RecruiterAnalysisChart, {
  ChartType
} from 'components/charts/RecruiterAnalysisChart';
import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { Card, Form } from 'react-bootstrap';
import AnalysisTable from './table/AnalysisTable';
import { Props, TableData } from './jobAnalysis.type';
import { getPreviousMonthsByCount } from 'helpers/date-utils';

function JobAnalysis({ data }: Props) {
  const [initialChartData, setInitialChartData] = useState<
    Record<ChartType, number[]>
  >({
    applications: [],
    rejected: [],
    shortlist: []
  });
  const [chartData, setChartData] =
    useState<Record<ChartType, number[]>>(initialChartData);
  const [tableTable, setTableData] = useState<TableData>([]);

  const last6Months = useMemo(() => {
    return getPreviousMonthsByCount(6);
  }, []);

  const charts = useMemo(() => Object.keys(chartData), [chartData]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value as ChartType;
    setChartData(prev => {
      const prevState = { ...prev };
      if (prevState[value]) {
        delete prevState[value];
      } else {
        prevState[value] = initialChartData[value];
      }
      return prevState;
    });
  };

  const updateChartData = () => {
    const formattedChartData: {
      [key: string]: number[];
    } = {};
    const applicationsMonthsReport: {
      [key: string]: number;
    } = {};
    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        for (const month of last6Months) {
          const monthData = data[key][month];
          const value = Object.values(monthData).reduce(
            (acc, item) => acc + item,
            0
          );
          if (key === 'applications') {
            for (const monthDataKey in monthData) {
              if (
                Object.prototype.hasOwnProperty.call(monthData, monthDataKey)
              ) {
                if (applicationsMonthsReport[monthDataKey]) {
                  applicationsMonthsReport[monthDataKey] +=
                    monthData[monthDataKey];
                } else {
                  applicationsMonthsReport[monthDataKey] =
                    monthData[monthDataKey];
                }
              }
            }
          }

          if (formattedChartData[key]) {
            formattedChartData[key].push(value || 0);
          } else {
            formattedChartData[key] = [value || 0];
          }
        }
      }
    }
    setInitialChartData(formattedChartData as Record<ChartType, number[]>);
    setChartData(formattedChartData as Record<ChartType, number[]>);
    setTableData(
      Object.entries(applicationsMonthsReport).map(([label, value]) => ({
        label,
        value
      }))
    );
  };

  useEffect(() => {
    if (data) {
      updateChartData();
    }
  }, [data]);

  return (
    <Card>
      <Card.Body>
        <header className="d-flex justify-content-between mb-2">
          <h4 className="text-body-tertiary">Top Active Jobs</h4>
          <small className="text-primary fw-bold">Last 6 Months</small>
        </header>
        <div className="d-flex gap-4">
          <Form.Check
            type="switch"
            label="Application"
            value="applications"
            checked={charts.includes('applications')}
            onChange={handleChange}
          />
          <Form.Check
            type="switch"
            label="Shortlisted"
            value="shortlist"
            checked={charts.includes('shortlist')}
            onChange={handleChange}
          />
          <Form.Check
            type="switch"
            label="Rejected"
            value="rejected"
            checked={charts.includes('rejected')}
            onChange={handleChange}
          />
        </div>
        <RecruiterAnalysisChart data={chartData} labels={last6Months} />
        <AnalysisTable data={tableTable} />
      </Card.Body>
    </Card>
  );
}

export default JobAnalysis;
